import config, { BACKEND_URL } from "../config";
import { isThirdPartyCheckout } from "./Bundles/RenderOnStore/utilities";
import { CART_CHANGE_ANALYZED, CART_CHANGE_EVENT } from "./constants";

class CartWatcherClass {
    cart: any = null;
    showCompareAtPrices: boolean = false;
    compareAtPrices: Map<number, { amount: number; has: "YES" | "NO" }> = new Map();
    addProductCollections: boolean = false;
    productCollections: Map<number, { collections: string[]; has: "YES" | "NO" }> = new Map();

    async init() {
        try {
            this.observeCartChanges();
            await this.fetchCart();
            if (!this?.cart?.token?.includes("?key=")) {
                await this.updateCart({ note: "" });
                // Ensure cart data is loaded before proceeding
                await this.fetchCart();
            }
            if (window.kefi_bundles.discount_legacy_flow) {
                this.removeCartNonLegacyDiscountAttributes();
            }
        } catch (error) {
            console.error("CartWatcher init error:", error);
        }
    }

    async loadCompareAtPrices(ids: string[]) {
        if (ids.length === 0) {
            return this.compareAtPrices;
        }
        const myHeaders = new Headers();
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("x-shopify-storefront-access-token", window.kefi_bundles.sft);

        ids = ids.map((id: string) => `"gid://shopify/ProductVariant/${id}"`);

        const graphql = JSON.stringify({
            query: `
        query GetVariantsWithProductData {
            nodes(ids: [${ids}]) {
                ... on ProductVariant {
                    id
                    compareAtPrice {
                        amount
                    }
                    product {
                        id
                        collections(first: 50) {
                            nodes {
                                id
                            }
                        }
                    }
                }
            }
        }
        `,
        });

        const response = await fetch("/api/2025-07/graphql.json", {
            method: "POST",
            headers: myHeaders,
            body: graphql,
        });

        if (!response.ok) {
            throw new Error("Failed to load compare at prices");
        }
        const data = await response.json();

        data.data.nodes.forEach((variant: any) => {
            if (variant.compareAtPrice) {
                this.compareAtPrices.set(+variant.id.split("/").pop(), {
                    amount: variant.compareAtPrice.amount * 100,
                    has: "YES",
                });
            } else {
                this.compareAtPrices.set(+variant.id.split("/").pop(), { amount: 0, has: "NO" });
            }

            if (variant.product.collections && variant.product.collections.nodes) {
                const colls = variant.product.collections.nodes.map((node: any) => node.id);
                this.productCollections.set(+variant.id.split("/").pop(), {
                    collections: colls,
                    has: "YES",
                });
            } else {
                this.productCollections.set(+variant.id.split("/").pop(), { collections: [], has: "NO" });
            }
        });

        return this.compareAtPrices;
    }

    async fetchCart() {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        if (response.bodyUsed) {
            await this.fetchCart();
            return this.cart;
        } else {
            this.cart = await response.json();
        }

        if (this.showCompareAtPrices || this.addProductCollections) {
            this.cart.original_total_price = 0;

            await this.loadCompareAtPrices(
                this.cart.items
                    .filter((item: CartLineItem) => !this.compareAtPrices.has(item.variant_id))
                    .map((item: any) => item.variant_id),
            );

            this.cart.items.forEach((item: CartLineItem) => {
                if (this.compareAtPrices.get(item.variant_id)?.has === "YES") {
                    item.original_price = this.compareAtPrices.get(item.variant_id)?.amount || item.original_price;
                    item.original_line_price =
                        (this.compareAtPrices.get(item.variant_id)?.amount || 0) * item.quantity ||
                        item.original_line_price;
                }
                if (this.productCollections.get(item.variant_id)?.has === "YES") {
                    item.collections = this.productCollections.get(item.variant_id)?.collections || [];
                }
                this.cart.original_total_price += item.original_line_price || 0;
            });
        }
        return this.cart;
    }

    async clearCart() {
        const response = await fetch(`${window.Shopify.routes.root}cart/clear.js`);

        if (!response.ok) {
            const oldData = this.cart;
            this.cart = await response.json();
            return { data: { ...oldData }, error: { ...this.cart } };
        }

        this.cart = await response.json();
        return { data: this.cart, error: {} };
    }

    async changeCart(updates: any) {
        const response = await fetch(`${window.Shopify.routes.root}cart/change.js`, {
            method: "POST",
            body: JSON.stringify({
                ...updates,
            }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });
        if (!response.ok) {
            const oldData = this.cart;
            this.cart = await response.json();
            return { data: { ...oldData }, error: { ...this.cart } };
        }

        this.cart = await response.json();
        return { data: this.cart, error: {} };
    }

    async updateCart(updates: any) {
        const response = await fetch(`${window.Shopify.routes.root}cart/update.js`, {
            method: "POST",
            body: JSON.stringify({
                ...updates,
            }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });
        if (!response.ok) {
            const oldData = this.cart;
            this.cart = await response.json();
            return { data: { ...oldData }, error: { ...this.cart } };
        }

        this.cart = await response.json();
        return { data: this.cart, error: {} };
    }

    async analyzeCartForDiscounts() {
        this.sendCart({
            token: this.cart.token,
            localization: {
                ...window.kefi_bundles.localization,

                customer_spent: window.kefi_bundles.customer_spent,

                customer_tags: window.kefi_bundles.customer_tags,
                time: new Date().toISOString(),
            },
            cartTransformNA: isThirdPartyCheckout(),
        });
    }

    observeCartChanges() {
        document.addEventListener(CART_CHANGE_EVENT, (e: any) => {
            const { detail } = e;
            console._log("Cart change event received", detail);
            const { isCartLinesUpdating } = detail;
            if (isCartLinesUpdating) {
                if (window.kefi_bundles.discount_legacy_flow) {
                    document.dispatchEvent(
                        new CustomEvent(CART_CHANGE_ANALYZED, { detail: { isCartLinesUpdating: true } }),
                    );
                } else {
                    this.analyzeCartForDiscounts();
                }
            } else {
                document.dispatchEvent(
                    new CustomEvent(CART_CHANGE_ANALYZED, { detail: { isCartLinesUpdating: false } }),
                );
            }
        });
    }

    async sendCart(cart: any) {
        const shop = window.kefi_bundles.shop;
        const appName = config.appName;
        await fetch(`${BACKEND_URL}/public/${appName}/bundles/discounts?shop=${shop}`, {
            method: "post",
            body: JSON.stringify({ cart }),
            headers: {
                "content-type": "application/json",
            },
        });
        if (window.__cartAnalyzedResolve) {
            window.__cartAnalyzedResolve();
            document.dispatchEvent(new CustomEvent(CART_CHANGE_ANALYZED, { detail: { isCartLinesUpdating: true } }));
        }
    }

    async removeCartNonLegacyDiscountAttributes() {
        await fetch("/cart/update.js", {
            method: "POST",
            body: JSON.stringify({
                attributes: {
                    __shopify_meta1: null,
                    __shopify_meta2: null,
                },
            }),
            headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
            },
        });
    }
}

export interface CartLineItem {
    id: number;
    properties: Record<string, any>;
    quantity: number;
    variant_id: number;
    key: string;
    title: string;
    price: number;
    original_price: number;
    presentment_price: number;
    discounted_price: number;
    line_price: number;
    original_line_price: number;
    total_discount: number;
    discounts: {
        amount: number;
        title: string;
    }[];
    sku: string;
    grams: number;
    vendor: string;
    taxable: boolean;
    product_id: number;
    product_has_only_default_variant: boolean;
    gift_card: boolean;
    final_price: number;
    final_line_price: number;
    url: string;
    featured_image: {
        aspect_ratio: number;
        alt: string;
        height: number;
        url: string;
        width: number;
    };
    image: string;
    handle: string;
    requires_shipping: boolean;
    product_type: string;
    product_title: string;
    product_description: string;
    variant_title: string;
    variant_options: string[];
    options_with_values: {
        name: string;
        value: string;
    }[];
    line_level_discount_allocations: {
        amount: number;
        discount_application: {
            type: string;
            key: string;
            title: string;
            description: string | null;
            value: string;
            created_at: string;
            value_type: string;
            allocation_method: string;
            target_selection: string;
            target_type: string;
            total_allocated_amount: number;
        };
    }[];
    line_level_total_discount: number;
    quantity_rule: {
        min: number;
        max: number | null;
        increment: number;
    };
    has_components: boolean;
    collections?: string[];
}

export type Cart = {
    items: Array<CartLineItem>;
    total_price: number;
};

const cartWatcher = new CartWatcherClass();

export default cartWatcher;
